<template>
  <div class="widget-body">
    <c-loader v-if="isLoading" />

    <b-table-simple
      v-else-if="!isError"
      dark
      striped
      bordered
      sticky-header
      :aria-label="i18n('head.relativePerformance', widgetNamespace)"
    >
      <b-thead>
        <b-tr>
          <b-th></b-th>
          <b-th colspan="2">{{ i18n('head.beta', widgetNamespace) }}</b-th>
          <b-th colspan="2">{{ i18n('head.correlation', widgetNamespace) }}</b-th>
          <b-th colspan="3">{{ i18n('head.relativePerformance', widgetNamespace) }}</b-th>
        </b-tr>
        <b-tr>
          <b-th>{{ i18n('head.name', widgetNamespace) }}</b-th>
          <b-th>1 {{ i18n('head.month', widgetNamespace) }}</b-th>
          <b-th>1 {{ i18n('head.year', widgetNamespace) }}</b-th>
          <b-th>1 {{ i18n('head.month', widgetNamespace) }}</b-th>
          <b-th>1 {{ i18n('head.year', widgetNamespace) }}</b-th>
          <b-th>1 {{ i18n('head.week', widgetNamespace) }}</b-th>
          <b-th>1 {{ i18n('head.month', widgetNamespace) }}</b-th>
          <b-th>1 {{ i18n('head.year', widgetNamespace) }}</b-th>
        </b-tr>
      </b-thead>
      <b-tbody>
        <b-tr
          v-for="row in tableData"
          :key="row['q.RIC']"
          :class="{ 'bg-selected': row['q.RIC'] === isSelected }"
          @click="onRowSelected(row['q.RIC'])"
        >
          <b-td v-for="fid in fids" :key="fid" :style="formatCell(fid, row[fid])">
            <v-fidlet :data="row[fid] || ''" :type="formats[fid]" />
          </b-td>
        </b-tr>
      </b-tbody>
    </b-table-simple>

    <v-alert show v-else variant="red" align="center">{{
      i18n('messages.error', 'widgets.indicesPerformanceCorrelation')
    }}</v-alert>
  </div>
</template>

<script>
import CLoader from '@custom/CLoader';
import widgetCommons from '@mixins/widgetCommons';
import { API } from '@/state/api';
import { contrast, convertRGBAtoRGB } from '@/utils/colors';
import { getConsolidatedRic } from '@/utils/helpers';

export default {
  name: 'w-indices-performance-correlation-widget-body',
  mixins: [widgetCommons],
  components: {
    CLoader
  },
  props: {
    wid: {
      type: String,
      required: true
    },
    ric: {
      type: String,
      required: true
    }
  },
  data() {
    return {
      extremes: {},
      fids: [
        'q._DSPLY_NAME',
        'relr._BETA_1M',
        'relr._BETA_1Y',
        'relr._COR_1M',
        'relr._COR_1Y',
        'relr._RPERF_1W',
        'relr._RPERF_1M',
        'relr._RPERF_1Y'
      ],
      formats: {},
      isError: false,
      isLoading: true,
      isSelected: null,
      retryOnErrorTime: 60, //s
      retryTimeout: 0,
      tableData: []
    };
  },
  watch: {
    ric: {
      handler() {
        this.fetch();
      },
      immediate: true
    }
  },
  computed: {
    widgetNamespace() {
      return 'widgets.indicesPerformanceCorrelation';
    },
    theme() {
      return this.$store.state.applicationSettings.general.settings.theme;
    },
    colorRange() {
      // we could use document.body here, but then this prop wouldn't react to theme changes
      const container = document.querySelector(`.theme--${this.theme}`);
      const background = this.theme === 'dark' ? [0, 0, 0] : [255, 255, 255];
      const styles = getComputedStyle(container);

      return {
        background,
        factormin: styles
          .getPropertyValue('--orange-lighten-1-rgb')
          .trim()
          .split(','),
        factormax: styles
          .getPropertyValue('--primary-rgb')
          .trim()
          .split(','),
        pctmin: styles
          .getPropertyValue('--negative-lighten-1-rgb')
          .trim()
          .split(','),
        pctmax: styles
          .getPropertyValue('--positive-lighten-1-rgb')
          .trim()
          .split(',')
      };
    }
  },
  methods: {
    onRowSelected(ric) {
      this.sendLinkUpdate(ric);
      if (this.isSelected === ric) {
        this.isSelected = null;
      } else {
        this.isSelected = ric;
      }
    },
    formatCell(fid, val) {
      if (fid === 'q._DSPLY_NAME') return;
      if (!val) return {};

      const dir = val > 0 ? 'max' : 'min';
      const alpha = val / this.extremes[fid][dir];
      const type = ['relr._RPERF_1W', 'relr._RPERF_1M', 'relr._RPERF_1Y'].includes(fid) ? 'pct' : 'factor';

      const background = convertRGBAtoRGB(this.colorRange[type + dir], alpha, this.colorRange.background);
      // calculate contrast for black text color
      const ratio = contrast(this.colorRange.background, background);
      // check if contrast is >= 4.5 if so, use black as text color else use white
      const color = ratio >= 4.5 ? 'var(--black)' : 'var(--white)';

      return {
        color,
        'background-color': `rgb(${background.join(',')})`
      };
    },
    getIndices() {
      return API.Rest.get(`/index/indicesbyconstituent?ric=${getConsolidatedRic(this.ric)}&fids=q.RIC`).then(
        response => {
          return response.data.data.map(entity => entity['q.RIC']);
        }
      );
    },
    getRatios(indices) {
      API.Rest.get(
        `/quote/relativeratios?ric=${this.ric}&benchmarkRics=${indices}&fids=${
          this.fids
        },q.RIC&date=${new Date().toJSON().substr(0, 10)}`
      ).then(response => {
        this.extremes = {};
        this.tableData = response.data.data.map(dataRow => {
          return Object.keys(dataRow).reduce((acc, fid) => {
            this.formats[fid] = response.data.metadata[fid].type;
            if (['q.RIC', 'q._DSPLY_NAME'].includes(fid)) acc[fid] = dataRow[fid];
            else {
              acc[fid] = parseFloat(dataRow[fid]);
              if (isNaN(acc[fid])) acc[fid] = '-';
              else {
                if (!this.extremes[fid]) this.extremes[fid] = { min: 0, max: 0 };
                acc[fid] = Math.round(acc[fid] * 1000) / 1000;
                const dir = acc[fid] < 0 ? 'min' : 'max';
                this.extremes[fid][dir] = Math[dir](this.extremes[fid][dir], acc[fid]);
              }
            }
            return acc;
          }, {});
        });
        this.isLoading = false;
        // console.warn(this.tableData);
      });
    },
    fetch() {
      clearTimeout(this.retryTimeout);
      this.isLoading = true;
      this.isError = false;
      this.isSelected = null;
      this.getIndices()
        .then(this.getRatios)
        .catch(e => {
          console.error(e);
          this.isError = true;
          this.retryTimeout = setTimeout(() => {
            this.fetch();
          }, this.retryOnErrorTime * 1000);
          this.isLoading = false;
        });
    },
    i18n(subkey) {
      return this.$t(`white-label.${'widgets.indicesPerformanceCorrelation'}.${subkey}`);
    }
  }
};
</script>
